import { Box, Divider, Link as MuiLink, Popover, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { LaseredDeductibleChip } from "@/components/ui/mui/LaseredDeductibleChip";
import type { DeductibleSource } from "@/resources/claimants/hooks/useCurrentPolicyData";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatLongDate } from "@/utils/formatters/dateFormatter";
import { policyDisplayLabel, useStoplossClaimContext } from "../../hooks/useStoplossClaimContext";

const Fact = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.5 }}>
    <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>{label}</Typography>
    <Typography component="div" sx={{ fontSize: "0.8125rem", fontWeight: 600, textAlign: "right" }}>
      {value}
    </Typography>
  </Box>
);

/** Plain-language source of the applied deductible. */
const SOURCE_TEXT: Record<DeductibleSource, string> = {
  BASE: "Policy base deductible",
  LASER: "Straight laser on this claimant",
  CONDITIONAL: "Conditional laser, triggered by a claim line",
  MODIFICATION: "Deductible modification",
  REVIEW_DECISION: "Chosen in a deductible review",
  CONTRACT: "Contract laser",
  EXCLUDED: "Excluded from the contract",
};

/**
 * What the date on the resolved deductible means, per source (policy_data.sql,
 * calc-expert review 2026-10-26): the policy start for the base, the laser's
 * effective date for a straight laser, the triggering line's service date for a
 * conditional laser, the chosen candidate's date for a review decision. The
 * overage itself is not date-windowed, so "since" would overstate it.
 */
const DATE_LABEL: Partial<Record<DeductibleSource, string>> = {
  BASE: "Policy start",
  LASER: "Laser effective",
  CONDITIONAL: "Triggered on",
  REVIEW_DECISION: "Chosen candidate dated",
  MODIFICATION: "Took effect",
};

/**
 * Peek at how the deductible applied to this claimant was resolved (#7582),
 * anchored to the source word in the rail caption so the analyst does not leave
 * the claim. Reads only what the page already holds: the claimant's policy
 * data and the policy. The claimant page stays the place to change anything.
 *
 * `lasered_attributes` is the straight laser only (claimants.go:2828-2836); it
 * is shown only when the straight laser is the source, since a conditional
 * laser or contract tier that outranks it would otherwise sit beside a stale
 * figure. "Laser effective" below is `effective_deductible_date`, which is the
 * laser's effective date when the laser is the source; `laser_reason` lives in
 * the claimant's policy_data JSONB (lasered-claimants endpoint), not on the
 * snapshot, so it is not shown here (#7630).
 */
export const DeductiblePeek = ({ anchorEl, onClose }: { anchorEl: HTMLElement | null; onClose: () => void }) => {
  const { displayClaimantId, policy, policyData, effectiveDeductible, isLasered, isASD, isFamily, reviewPending } =
    useStoplossClaimContext();
  const source = effectiveDeductible?.source ?? "BASE";
  const laser = policyData?.lasered_attributes;
  const base = policy?.specific_benefit?.specific_deductible;
  const excluded = source === "EXCLUDED";
  const contract = source === "CONTRACT";
  const dateLabel = DATE_LABEL[source];

  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{ paper: { sx: { width: 380, p: 2, borderRadius: 3 } } }}
    >
      <Box data-testid="deductible-peek">
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
          {isFamily ? "Deductible on this policy" : "Deductible applied to this claimant"}
        </Typography>
        <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mb: 1 }}>
          {policyDisplayLabel(policy)}
        </Typography>
        <Stack divider={<Divider />}>
          <Fact
            label="Applied"
            value={
              excluded ? (
                "Excluded"
              ) : (
                <>
                  {formatUSD(effectiveDeductible?.amount ?? 0)}
                  {isLasered ? (
                    <>
                      {" "}
                      <LaseredDeductibleChip />
                    </>
                  ) : null}
                </>
              )
            }
          />
          <Fact label="Source" value={SOURCE_TEXT[source]} />
          {dateLabel && policyData?.effective_deductible_date ? (
            <Fact label={dateLabel} value={formatLongDate(policyData.effective_deductible_date)} />
          ) : null}
          {!excluded && typeof base === "number" && base !== effectiveDeductible?.amount ? (
            <Fact label="Policy base deductible" value={formatUSD(base)} />
          ) : null}
          {source === "LASER" && laser?.amt_lasered_deductible ? (
            <Fact label="Straight laser" value={formatUSD(laser.amt_lasered_deductible)} />
          ) : null}
          {contract && policyData?.amt_contract_lasered_deductible ? (
            <Fact label="Contract laser" value={formatUSD(policyData.amt_contract_lasered_deductible)} />
          ) : null}
          {(contract || excluded) &&
          (policyData?.contract_incurred_start_date || policyData?.contract_incurred_end_date) ? (
            <Fact
              label="Contract incurred window"
              value={`${policyData.contract_incurred_start_date ? formatLongDate(policyData.contract_incurred_start_date) : "…"} to ${
                policyData.contract_incurred_end_date ? formatLongDate(policyData.contract_incurred_end_date) : "…"
              }`}
            />
          ) : null}
          {(contract || excluded) && (policyData?.contract_paid_start_date || policyData?.contract_paid_end_date) ? (
            <Fact
              label="Contract paid window"
              value={`${policyData.contract_paid_start_date ? formatLongDate(policyData.contract_paid_start_date) : "…"} to ${
                policyData.contract_paid_end_date ? formatLongDate(policyData.contract_paid_end_date) : "…"
              }`}
            />
          ) : null}
        </Stack>
        {reviewPending ? (
          <Typography sx={{ fontSize: "0.75rem", color: "warning.dark", mt: 1 }}>
            A deductible review is open; this is the last resolved amount, held in force.
          </Typography>
        ) : null}
        {isASD ? (
          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 1 }}>
            Aggregate-specific policy: part of the excess over this deductible is absorbed by the aggregate specific
            deductible. The claim is the carrier-payable share.
          </Typography>
        ) : null}
        {isFamily ? (
          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 1 }}>
            Family deductible policy: the claim is sized on the family unit's pool, not this claimant's deductible
            alone.
          </Typography>
        ) : null}
        {displayClaimantId ? (
          <Typography sx={{ fontSize: "0.8125rem", mt: 1.5 }}>
            <MuiLink component={RouterLink} to={`/claimants/${displayClaimantId}/show/deductibles`} underline="hover">
              Manage on the claimant page
            </MuiLink>
          </Typography>
        ) : null}
      </Box>
    </Popover>
  );
};
